import React, { useEffect, useMemo, useState } from "react";
import {
  FormSections,
  ProgramContainer,
} from "../../entities/formTemplateDataType";
import FormValidationUtils from "../../utilities/FormValidationUtils";
import axios from "axios";
import DataUtils, {
  sanitizeEmail,
  sanitizeString,
} from "../../utilities/DataUtils";
import ReCaptchaV3 from "../captcha/CaptchComponent";
import ReCaptchaButton from "../captcha/captcha-button";
import {
  getCxpMessages,
  getUserProfileFromSession,
} from "../../services/CxpProfileService";
import { processPayload } from "../../utilities/custom/ODRUtils";
import { useProfile } from "../../services/ProfileHooks";
import Utils from "../../utilities/Utils";
import SectionUtils from "../../utilities/SectionUtils";
import TrackTaskCompletion, {
  getSubmissionId,
} from "../../utilities/TrackTaskCompletion";

interface Props {
  token: string;
  programContainer: ProgramContainer;
  isEng: boolean;
  section: FormSections;
  onButtonClick: (data: any) => void;
  callback?: (p: ProgramContainer) => void;
  callbackStepIndex?: (index: number, returnIndex?: number) => void;
}

const ODRHandleSubmitComponent: React.FC<Props> = ({
  programContainer,
  isEng,
  section,
  onButtonClick,
  callback,
  callbackStepIndex,
}) => {
  const [validationErrMsg, setValidationErrMsg] = useState<any[]>([]);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [confirmationVisible, setConfirmationVisible] =
    useState<boolean>(false);
  const [summaryVisible, setSummaryVisible] = useState<boolean>(true);
  const [transactionId, setTransactionId] = useState<string>("");
  const [uploadReceiptSuccess, setUploadReceiptSuccess] =
    useState<boolean>(false);
  const [submissionError, setSubmissionError] = useState<boolean>(false);

  const cleanupAfterSubmit = () => {
    if (!programContainer.ClearFormAfterSubmit) {
      return;
    }
    DataUtils.clearFormValue(programContainer);
    SectionUtils.resetDirtyFlags(programContainer);
  };

  const globalProfile = useProfile();
  const data = DataUtils.collectFormValue(programContainer);

  const updateErrorResponse = (data: any, isOverrideMessage?: boolean) => {
    let newContainer = { ...programContainer } as ProgramContainer;
    const errorSection = newContainer?.ListOfFormSections?.find(
      (formSection) => formSection?.IsTechErrorPage,
    );
    if (errorSection) {
      errorSection.apiResponse = data;
      const errorElement = errorSection.ListOfFormSubSections.flatMap(
        (subSection) => subSection?.ListOfFormSectionElements || [],
      ).find((element) => element?.LoadBindName === "errorMessageContent");

      if (errorElement && isOverrideMessage) {
        errorElement.Value = data?.messageEn;
        errorElement.ValueFRA = data?.messageFr;
      }
      console.debug("Error in ODR Handle Submit:", data);
    }

    const surveyUrlParams =
      newContainer.ListOfFormSections[5].ListOfFormSubSections[2]
        .ListOfFormSectionElements[19].Value;
    newContainer.ListOfFormSections[5].ListOfFormSubSections[2].ListOfFormSectionElements[19].Value =
      {
        ...surveyUrlParams,
        ...(Utils.isNotNullOrEmpty(data?.errorCode) && {
          code: data.errorCode,
        }),
        sts: "400",
      };
    callback(newContainer);
  };

  const shouldOverrideMessage = (response: any): boolean => {
    return (
      response?.status === 200 &&
      response?.data?.isHardstop &&
      response?.data?.errorCode?.length > 0
    );
  };

  const handleSubmit = (token: string) => {
    let invalidControlList = FormValidationUtils.invalidControls(section);
    const language = isEng ? "EN" : "FR";

    if (invalidControlList.length > 0) {
      setValidationErrMsg(invalidControlList);
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(
        () => document.getElementById("error-container")?.focus(),
        100,
      );
    } else {
      const url =
        programContainer.APIBasePath +
        (data?.journeyType === "withdraw"
          ? programContainer.DeleteAPI
          : programContainer.UpdateAPI);

      const profile = getUserProfileFromSession();

      const payload = processPayload(data);
      let apiPayload = {};
      if (data?.journeyType === "withdraw") {
        apiPayload = {
          ...payload,
          withdrawal: true,
        };
      } else {
        apiPayload = {
          ...payload,
        };
      }

      apiPayload = {
        ...apiPayload,

        language: language,

        transactionId: sanitizeString(data?.verifyApiData?.transactionId) || "",
        externalTransactionID:
          sanitizeString(data?.verifyApiData?.externalTransactionID) || "",
        publicSecureIdentifier: profile?.userId || null,
        token: token,

        emailFor: data?.journeyType || "",
        receiver: sanitizeEmail(profile?.email) || "",
        donorName: data?.donorName || "",
      };

      setIsBusy(true);

      axios
        .post(url, apiPayload)
        .then((res) => {
          if (res?.status !== 200 || res?.data?.errorCode?.length > 0) {
            updateErrorResponse(res?.data, shouldOverrideMessage(res));

            setSummaryVisible(false);
            setSubmissionError(true);
            setIsBusy(false);

            return;
          }
          setConfirmationVisible(true);
          setSummaryVisible(false);
          setTransactionId(data?.verifyApiData?.transactionId || "");
          setUploadReceiptSuccess(res?.data?.uploadReceiptSuccess || true);

          const odrActionMap: Record<string, "new" | "update" | "withdrawal"> =
            {
              registration: "new",
              update: "update",
              withdraw: "withdrawal",
            };
          TrackTaskCompletion(programContainer, {
            serviceName: "organ donor registry",
            serviceItems: "organ donation",
            serviceAction: odrActionMap[data?.journeyType] || "update",
            value: "0.00",
            submissionId: getSubmissionId("odr"),
          });

          DataUtils.clearDraft(
            programContainer.CategoryId,
            globalProfile?.email,
            programContainer.draftId,
          );
          programContainer.draftId = undefined;
          cleanupAfterSubmit();

          //FIXME Messages will like to be refreshed after submit
          if (globalProfile?.userId && globalProfile?.token) {
            getCxpMessages(globalProfile?.token, {
              pageNumber: 1,
              pageSize: 20,
              search: "",
            })
              .then((messages) => {
                console.log(
                  "Refresh Cxp inbox messages after submit:",
                  messages,
                );
                if (messages?.messages) {
                  const unread1 =
                    messages?.messages?.filter((msg: any) => !msg?.isRead)
                      .length ?? 0;
                  const unread2 =
                    globalProfile?.messages?.filter((msg: any) => !msg?.isRead)
                      .length ?? 0;
                  if (unread1 !== unread2) {
                    programContainer = {
                      ...programContainer,
                      Profile: {
                        ...globalProfile,
                        messages: messages?.messages,
                      },
                    };
                    // FIXME revert this logic once the global profile is updated through programContainer
                    //programContainer.Profile.messages = messages?.messages;
                    callback(programContainer);
                  }
                }
              })
              .catch((ex) => {
                console.log("Error refreshing Cxp Messages", ex);
              })
              .finally(() => {
                setIsBusy(false);
              });
          }
        })
        .catch((err) => {
          setIsBusy(false);

          updateErrorResponse(
            err.response?.data,
            shouldOverrideMessage(err?.response),
          );

          setSummaryVisible(false);
          setSubmissionError(true);
          console.log(err);
        })
        .finally(() => {
          setIsBusy(false);
        });
    }
  };
  const props = useMemo(
    () => ({
      isBusy,
      confirmationVisible,
      summaryVisible,
      transactionId,
      uploadReceiptSuccess,
      submissionError,
      validationErrMsg,
    }),
    [isBusy, validationErrMsg],
  );

  useEffect(() => {
    onButtonClick(props);
  }, [props]);

  useEffect(() => {
    return () => {
      if (data.journeyType === "withdraw") {
        programContainer.ListOfFormSections[4].Visible = true;
      }
    };
  }, []);

  const processStepperScript = (currentSection) => {
    let destinationIndex = null;
    if (currentSection?.CancelButtonScript) {
      const customJSFunc = eval(`(${currentSection.CancelButtonScript})`);
      destinationIndex = customJSFunc(currentSection);
    }
    return destinationIndex;
  };

  return (
    <div className="ontario-margin-top-24-!">
      <ReCaptchaV3 lang={isEng ? "en" : "fr"}>
        <ReCaptchaButton
          key={validationErrMsg?.toString()}
          title={isEng ? "Submit" : "Soumettre"}
          action="submitODR"
          buttonHandler={handleSubmit}
        >
          {isEng ? "Submit" : "Soumettre"}
        </ReCaptchaButton>
      </ReCaptchaV3>
      {section?.IncludingCancelButton === "Y" && (
        <button
          className="ontario-button ontario-button--secondary"
          onClick={() => {
            if (section?.CancelButtonScript) {
              const stepperIndex = processStepperScript(section);
              callbackStepIndex(stepperIndex);
            }
          }}
        >
          {isEng ? "Cancel" : "Annuler"}
        </button>
      )}
    </div>
  );
};

export default ODRHandleSubmitComponent;
